import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import dayjs from '@app/utils/dayjs-setup';
import type { Dayjs } from 'dayjs';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { useQuery } from '@tanstack/react-query';
import { Alert, Empty, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAvailability, listItems, listRentals } from '../../api';
import type { ActiveRental, InventoryItem, RentalAvailability } from '../../types';
import AvailabilityCalendar from './AvailabilityCalendar';
import { buildMonthGrid } from './monthGrid';

const RENTALS_PAGE = 1000;

const InlineStat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <span className="flex items-baseline gap-1.5 text-sm">
    <span className="text-app-text-light">{label}</span>
    <span className="font-semibold tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</span>
  </span>
);

// Every rental of the item overlapping the grid, returned ones included, so a
// past day can still say who had the item.
const fetchGridRentals = async (itemId: number, from: string, to: string): Promise<ActiveRental[]> => {
  const rentals: ActiveRental[] = [];
  for (;;) {
    const page = await listRentals({
      status: 'all',
      item_id: itemId,
      from,
      to,
      offset: rentals.length,
      limit: RENTALS_PAGE,
    });
    rentals.push(...(page.data as ActiveRental[]));
    if (page.data.length === 0 || rentals.length >= page.total) return rentals;
  }
};

const AvailabilityTab: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useSmartErrorHandling();

  const [itemId, setItemId] = useState<number | null>(null);
  // Kept so the picker can render the chosen item's label without refetching.
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'));

  const [availability, setAvailability] = useState<RentalAvailability | null>(null);
  const [rentals, setRentals] = useState<ActiveRental[]>([]);
  const [loading, setLoading] = useState(false);

  // Whether the catalogue holds any rentable item at all. Without this the tab
  // is indistinguishable from broken: the picker below filters on is_rentable,
  // so with none configured it offers an empty list and the page just sits on
  // "select an item" forever, with nothing saying why there is nothing to pick.
  const { data: rentableCount, isLoading: isRentableCountLoading } = useQuery({
    queryKey: ['inventory-rentable-item-count'],
    queryFn: async () => (await listItems({ offset: 0, limit: 1, capability: 'is_rentable' })).total,
    staleTime: 60 * 1000,
  });
  const hasNoRentableItems = !isRentableCountLoading && rentableCount === 0;

  // The picker searches and pages server-side; only rentable items can be rented.
  const fetchRentableItems: ApiFetchFunction<InventoryItem> = useCallback(
    async (params: ApiQueryParams) => ({
      data: await listItems({ ...params, capability: 'is_rentable' }),
    }),
    []
  );

  const handleItemChange = useCallback((selection: InventoryItem | InventoryItem[] | undefined) => {
    const item = Array.isArray(selection) ? selection[0] : selection;
    setSelectedItem(item ?? null);
    setItemId(item?.itemId ?? null);
  }, []);

  const selectedItemsProvider = useCallback(() => selectedItem ?? undefined, [selectedItem]);

  // Exactly the span the calendar draws, leading and trailing days included, so
  // every box in the grid carries a real figure rather than a blank.
  const { gridStart, gridEnd } = useMemo(() => buildMonthGrid(month), [month]);

  useEffect(() => {
    if (itemId == null) {
      setAvailability(null);
      setRentals([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAvailability({ item_id: itemId, from: gridStart, to: gridEnd })
      .then((data) => {
        if (!cancelled) setAvailability(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setAvailability(null);
          handleError(e as Error, { customMessage: t('inventory-availability-load-failed') });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Separate from the free counts: without the rentals the calendar still answers "is it free".
    setRentals([]);
    fetchGridRentals(itemId, gridStart, gridEnd)
      .then((gridRentals) => {
        if (!cancelled) setRentals(gridRentals);
      })
      .catch((e) => {
        if (!cancelled) handleError(e as Error, { customMessage: t('inventory-availability-rentals-load-failed') });
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, gridStart, gridEnd, handleError, t]);

  const freeByDate = useMemo(() => {
    const map = new Map<string, number>();
    (availability?.points ?? []).forEach((point) => {
      map.set(dayjs(point.date).format('YYYY-MM-DD'), Number(point.free));
    });
    return map;
  }, [availability]);

  return (
    // min-h-0 + an inner scroller: the tab pane is a sized flex column (see
    // Inventory.module.css), so without it the calendar's lower weeks render
    // below the fold with nothing to scroll them into view.
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <Typography.Text type="secondary">{t('inventory-availability-intro')}</Typography.Text>

      {hasNoRentableItems && <Alert type="info" showIcon message={t('inventory-availability-no-rentable-items')} />}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div style={{ minWidth: 260 }}>
          <GenericSearchSelect<InventoryItem>
            className="w-full"
            allowClear
            disabled={hasNoRentableItems}
            value={itemId ?? undefined}
            apiFetch={fetchRentableItems}
            getItemId={(item) => item.itemId}
            getItemLabel={(item) => item.name}
            onSelectionChange={handleItemChange}
            selectedItemsProvider={selectedItemsProvider}
            placeholder={t('inventory-availability-item-picker')}
            searchPlaceholder={t('inventory-search-placeholder')}
          />
        </div>
        {itemId != null && (
          <>
            <InlineStat label={t('inventory-availability-owned')} value={Number(availability?.owned ?? 0)} />
            <InlineStat label={t('inventory-availability-free-now')} value={Number(availability?.freeNow ?? 0)} />
            <InlineStat
              // Scoped to the month on show: the endpoint answers the first free day in the range it was asked for.
              label={t('inventory-availability-next-free-in-view')}
              value={
                availability?.nextFreeDate
                  ? dayjs(availability.nextFreeDate).format('MMM DD, YYYY')
                  : t('inventory-availability-next-free-none')
              }
            />
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {itemId == null ? (
          !hasNoRentableItems && <Empty description={t('inventory-availability-select-prompt')} />
        ) : (
          <div className="flex flex-col gap-3">
            <AvailabilityCalendar
              month={month}
              freeByDate={freeByDate}
              rentals={rentals}
              owned={Number(availability?.owned ?? 0)}
              loading={loading}
              onMonthChange={setMonth}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityTab;
